import { Button, DatedList, Icon } from 'gu-ui-kit'
import PropTypes from 'prop-types'
import qs from 'qs'
import React, { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import prepareFilterParams from 'utils/prepareFilterParams'
import { useWalletsActions } from 'modules/Finance/Wallets/useWalletsActions'
import { useWallets } from 'modules/Finance/Wallets/useWallets'
import EmptyResults from 'components/EmptyResults'
import formatRelative from 'utils/dateFns/formatRelative'
import { Link } from 'react-router-dom'
import { ReactComponent as NoAdvertisersImage } from 'images/illustration/no-advertisers.svg'
import styles from 'modules/Finance/Wallets/subcomponents/Transaction/Transaction.module.scss'
import useGeneratePathI18n from 'utils/hooks/useGeneratePathI18n'
import { ROUTES } from 'constants/routes'
import TransactionRow from './subcomponents/TransactionRow'

const Transaction = () => {
  const { t } = useTranslation(['wallets'])
  const pathGen = useGeneratePathI18n()

  const {
    transactions: { data = [], pagination, loaded, loadFailed, loading },
    activeWalletID,
    filters,
    activeWallet: { financial_profile: { financial_profile_type: { type = null } = {} } = {} },
  } = useWallets()

  const { getWalletTransactions, refetchWalletTransaction } = useWalletsActions()

  const transactions = useMemo(
    () => data.map((item) => ({ ...item, date: item.created_at })),
    [data],
  )

  const handleGetMoreItems = (e) => {
    e.preventDefault()
    getWalletTransactions({ offset: pagination.offset + pagination.limit })
  }

  useEffect(() => {
    if (activeWalletID === null) {
      return null
    }

    refetchWalletTransaction({ offset: 0 })
  }, [activeWalletID, filters])

  const downloadLink = useMemo(() => {
    if (activeWalletID === null) {
      return null
    }
    const { name = '', ...rest } = prepareFilterParams(filters)
    const params = qs.stringify(
      { search: name, ...rest },
      {
        arrayFormat: 'brackets',
      },
    )
    return `/api/app/v1/wallets/${activeWalletID}/transactions-export?${params}`
  }, [activeWalletID, filters])

  return (
    <div className={styles['transaction']}>
      <DatedList
        emptyComponent={
          <EmptyResults
            header={t('transaction.no_history')}
            className={styles['transaction-empty']}
            image={<NoAdvertisersImage />}
            text={t('transaction.start')}
            actionButton={
              !type?.match(/(partner|bonus)/) && (
                <div className={styles['transaction-empty__button']}>
                  <Button
                    component={Link}
                    size="l"
                    variant="primary"
                    startIcon={<Icon name="card" />}
                    to={pathGen(ROUTES.BILLING_TOP_UP)}
                  >
                    {t('transaction.fillUp')}
                  </Button>
                </div>
              )
            }
          />
        }
        itemComponent={TransactionRow}
        pagination={pagination}
        className={styles['transaction__list']}
        items={transactions}
        moreButtonLabel={t('transaction.showMore')}
        handleGetMoreItems={handleGetMoreItems}
        loading={loading}
        loaded={loaded}
        loadFailed={loadFailed}
        downloadText={t('transaction.download')}
        downloadLink={downloadLink}
        l10n={{
          formatFunction: formatRelative,
          today: t('common::dated_list.date_format.today'),
          yesterday: t('common::dated_list.date_format.yesterday'),
          formatDates: {
            short: t('common::dated_list.date_format.short'),
            long: t('common::dated_list.date_format.long'),
          },
        }}
      />
    </div>
  )
}
Transaction.propTypes = {
  listMode: PropTypes.oneOf(['transactions', 'promised-payments']),
}

export default Transaction
